import {categoryLabel} from "../../global/categories";
import React, { useEffect, useState, useCallback } from "react";
import axios from "../../util/axiosConfig";
import { useAuth } from "../../global/AuthContext";
import { useNavigate } from "react-router-dom";
import "../../assets/css/component/factList/FactList.css";
import LevelBadge from "../../global/LevelBadge";

export type RecentFactItem = {
	id: number;
	title: string;
	category: string;
	createdAt: string;
	viewCount: number;
	authorNickname: string;
	authorLevel: number;

	sentimentPosCount: number;
	sentimentNegCount: number;
	sentimentNeuCount: number;

	posReplyCount: number;
	negReplyCount: number;
	neuReplyCount: number;

	commentCount: number;
};

type ApiResponse = {
	items: RecentFactItem[];
	total: number;
};

type RecentFactProps = {
	keyword: string;
	type: "recent" | "my" | "interest";
	category: string | null;
};

const RecentFact: React.FC<RecentFactProps> = ({
												   keyword,
												   type,
												   category,
											   }) => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const isLoggedIn = !!user;

	const [posts, setPosts] = useState<RecentFactItem[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize] = useState(10);
	const [total, setTotal] = useState(0);

	const [loading, setLoading] = useState(false);
	const [initialized, setInitialized] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/* ✅ 블록 페이지네이션 추가 */
	const blockSize = 10;
	const [pageBlock, setPageBlock] = useState(0);

	const maxPage = Math.max(1, Math.ceil(total / pageSize));

	const startPage = pageBlock * blockSize + 1;
	const endPage = Math.min(startPage + blockSize - 1, maxPage);

	const pageNumbers: number[] = [];
	for (let i = startPage; i <= endPage; i++) {
		pageNumbers.push(i);
	}

	const fetchPage = useCallback(
		async (targetPage: number) => {
			if ((type === "my" || type === "interest") && !isLoggedIn) {
				setInitialized(true);
				setPosts([]);
				setTotal(0);
				return;
			}

			try {
				setLoading(true);
				setError(null);

				const res = await axios.get<ApiResponse>("/api/board/search", {
					params: {
						page: targetPage,
						pageSize,
						type,
						keyword: keyword || undefined,
						category: category || undefined,
					},
				});

				setPosts(res.data.items);
				setTotal(res.data.total);
				setPage(targetPage);
			} catch (err) {
				console.error("Fact 검색 실패:", err);
				setError("게시글을 불러올 수 없습니다.");
			} finally {
				setLoading(false);
				setInitialized(true);
			}
		},
		[type, keyword, category, pageSize, isLoggedIn]
	);

	useEffect(() => {
		setPosts([]);
		setPage(1);
		setTotal(0);
		setInitialized(false);
		setPageBlock(0); // ✅ 검색/카테고리 변경 시 블록 초기화
		fetchPage(1);
	}, [fetchPage]);

	const handlePageClick = (p: number) => {
		fetchPage(p);
	};

	const goPrevBlock = () => {
		if (pageBlock > 0) {
			setPageBlock(pageBlock - 1);
		}
	};

	const goNextBlock = () => {
		if ((pageBlock + 1) * blockSize < maxPage) {
			setPageBlock(pageBlock + 1);
		}
	};

	if (!initialized) {
		return <div className="fact-skeleton">⏳ 불러오는 중...</div>;
	}

	return (
		<div className="fact-wrap">

			<div className="fact-header">
				<h2 className="fact-header-title">
					{category ? `${categoryLabel(category)} 팩트` : "전체 팩트"}
				</h2>

				<button
					className="fact-write-btn"
					onClick={() => {
						if (isLoggedIn) {
							navigate("/writeNew");
						} else {
							navigate("/login");
						}
					}}
				>
					팩트 작성
				</button>
			</div>

			{error && (
				<div className="fact-error">
					<p>{error}</p>
					<button onClick={() => fetchPage(1)}>다시 시도</button>
				</div>
			)}

			{!loading && posts.length === 0 && !error ? (
				<p className="fact-empty">게시글이 없습니다.</p>
			) : (
				<ul className="fact-list">
					{posts.map((item) => {
						const totalSentiments =
							item.sentimentPosCount +
							item.sentimentNeuCount +
							item.sentimentNegCount;

						const positivePct =
							totalSentiments > 0
								? Math.round((item.sentimentPosCount * 100) / totalSentiments)
								: 0;

						const neutralPct =
							totalSentiments > 0
								? Math.round((item.sentimentNeuCount * 100) / totalSentiments)
								: 0;

						const negativePct =
							totalSentiments > 0
								? Math.round((item.sentimentNegCount * 100) / totalSentiments)
								: 0;

						return (
							<li
								key={item.id}
								className="fact-row"
								onClick={() =>
									navigate(`/viewPost/${item.id}`, {
										state: { from: type },
									})
								}
							>
								<div className="fact-row-top">
									<div className="author">
										<LevelBadge level={item.authorLevel} />
										<span className="nickname">
											{item.authorNickname}
										</span>
									</div>
									<div className="meta">
										댓글 {item.commentCount} · 조회 {item.viewCount}
									</div>
								</div>

								<div className="fact-row-title">
									[{categoryLabel(item.category)}] {item.title}
								</div>

								<div className="fact-gauge">
									<div
										className="fact-gauge-pos"
										style={{ width: `${positivePct}%` }}
									/>
									<div
										className="fact-gauge-neu"
										style={{ width: `${neutralPct}%` }}
									/>
									<div
										className="fact-gauge-neg"
										style={{ width: `${negativePct}%` }}
									/>
								</div>

								<div className="fact-footer">
									<div className="footer-left">
										긍정근거 {item.posReplyCount} | 반대근거 {item.negReplyCount} | 기타 {item.neuReplyCount}
									</div>

									<div className="footer-center">
										긍정 {positivePct}% ({item.sentimentPosCount})
										· 보류 {neutralPct}% ({item.sentimentNeuCount})
										· 부정 {negativePct}% ({item.sentimentNegCount})
									</div>

									<div className="footer-right">
										{new Date(item.createdAt).toLocaleString()}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			)}

			{/* ✅ 블록형 페이지네이션 */}
			<nav className="fact-pagination">
				<button onClick={goPrevBlock} disabled={pageBlock === 0}>
					이전
				</button>

				{pageNumbers.map((p) => (
					<button
						key={p}
						className={p === page ? "active-page" : ""}
						onClick={() => handlePageClick(p)}
						disabled={loading}
					>
						{p}
					</button>
				))}

				<button
					onClick={goNextBlock}
					disabled={(pageBlock + 1) * blockSize >= maxPage}
				>
					다음
				</button>
			</nav>
		</div>
	);
};

export default RecentFact;